import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, X, MapPin } from "lucide-react";
import { PROJECTS } from "@/data/content";
import { scrollToSection, startScroll, stopScroll } from "@/lib/scroll";

export default function Projects() {
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (!active) return undefined;
    stopScroll();
    const onKey = (e) => {
      if (e.key === "Escape") setActive(null);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      startScroll();
      window.removeEventListener("keydown", onKey);
    };
  }, [active]);

  return (
    <section id="projekt" data-testid="projects-section" className="border-b border-white/10 py-20 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mb-14 sm:mb-16"
        >
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-neutral-500">03 / Projekt</p>
          <h2 className="mt-4 font-display text-4xl font-bold uppercase leading-none tracking-tight text-white sm:text-5xl">
            Se våra senaste projekt
          </h2>
          <p className="mt-4 max-w-lg text-sm leading-relaxed text-neutral-400">
            Från byte av fjärrvärmecentraler till rörsvetsning och installation – ett urval av våra arbeten i
            Storstockholm. Klicka på ett projekt för att se det större.
          </p>
        </motion.div>

        <div className="grid gap-5 md:grid-cols-12">
          {PROJECTS.map((project, i) => (
            <motion.button
              key={project.id}
              type="button"
              data-testid={`project-card-${project.id}`}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.14, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              onClick={() => setActive(project)}
              className={`group relative overflow-hidden border border-white/10 text-left transition-colors duration-300 hover:border-white/30 ${
                [0, 3, 4].includes(i) ? "md:col-span-7" : "md:col-span-5"
              }`}
            >
              <div className={`overflow-hidden ${[0, 3, 4].includes(i) ? "aspect-[16/10]" : "aspect-[16/10] md:aspect-[4/3.36]"}`}>
                <img
                  src={project.image}
                  alt={project.alt}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">
                  {project.type} — {project.location}
                </p>
                <div className="mt-1.5 flex items-center justify-between gap-4">
                  <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">
                    {project.title}
                  </h3>
                  <ArrowUpRight className="h-6 w-6 shrink-0 text-white/60 transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-white" />
                </div>
              </div>
            </motion.button>
          ))}

          <motion.button
            type="button"
            data-testid="project-card-cta"
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ delay: 0.2, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            onClick={() => scrollToSection("#kontakt")}
            className="steel-card hairline-t group flex items-center justify-between gap-6 border border-white/10 p-7 text-left transition-colors duration-300 hover:border-white/30 md:col-span-12 sm:p-9"
          >
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Nästa projekt</p>
              <h3 className="mt-2 font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-4xl">
                Kanske ditt? <span className="text-neutral-500">Hör av dig så pratar vi om det.</span>
              </h3>
            </div>
            <span className="flex h-12 w-12 shrink-0 items-center justify-center border border-white/20 transition-colors duration-300 group-hover:border-white group-hover:bg-white sm:h-14 sm:w-14">
              <ArrowUpRight className="h-5 w-5 text-white transition-colors duration-300 group-hover:text-black" />
            </span>
          </motion.button>
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="project-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-black/92 p-4 backdrop-blur-sm sm:p-8"
            onClick={() => setActive(null)}
            role="dialog"
            aria-modal="true"
            aria-label={active.title}
          >
            <motion.figure
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97, y: 8 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="max-h-full w-full max-w-5xl overflow-hidden border border-white/15 bg-coal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative">
                <img
                  src={active.image}
                  alt={active.alt}
                  className="max-h-[62vh] w-full object-cover"
                  data-testid="lightbox-image"
                />
                <button
                  type="button"
                  data-testid="lightbox-close-button"
                  onClick={() => setActive(null)}
                  className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center border border-white/25 bg-black/70 text-white backdrop-blur transition-colors duration-200 hover:bg-white hover:text-black"
                  aria-label="Stäng bildvisare"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <figcaption className="p-6 sm:p-8">
                <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                  <MapPin className="h-3.5 w-3.5 text-weld" />
                  {active.type} — {active.location}
                </p>
                <h3 className="mt-2 font-display text-3xl font-bold uppercase tracking-tight text-white">
                  {active.title}
                </h3>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-neutral-300 sm:text-base">
                  {active.description}
                </p>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
